import React, { useCallback, useState } from 'react';
import { StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { useMutation, useQueryClient } from 'react-query';

import APIService from '../../../../services/api.service';
import { AxiosError } from 'axios';
import { Item } from '../../types/item.types';
import { RefreshResponse } from '../../types/items.api';
import { ToastService } from '../../../../services/toast.service';
import refreshIcon from '@ingka/ssr-icon/paths/arrow-clockwise';
import { useConnectedItemsState } from '../../state/connected-items.state';
import { useParentItemsFromSelectedSubItems } from '../../state/selection.state';
import { useTableDataState } from '../../state/table-data.state';

export const RefreshConnectedButton = () => {
   const selectedItems = useParentItemsFromSelectedSubItems(true);
   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const [isProcessing, setIsProcessing] = useState(false);

   const { updateRefreshResponses, raiseRequestErrors, clearResult } = useConnectedItemsState((state) => ({
      updateRefreshResponses: state.updateRefreshResponses,
      raiseRequestErrors: state.raiseRequestErrors,
      clearResult: state.clearResult
   }));
   const piaRefreshState = useConnectedItemsState();

   const { mutate } = useMutation(
      (items: Item[]) => APIService.refreshItems(items, false),
      {
         onMutate: (mutationItems: Item[]) => {
            setIsProcessing(true);
            clearResult();

            const itemNos = mutationItems.map((item: Item) => item.itemNo.toString());
            piaRefreshState.updateRefreshRequests(itemNos, 'refresh');
         },
         onSuccess: (response: RefreshResponse) => {
            updateRefreshResponses([response]);
            queryClient.invalidateQueries(['data', query]);

            setTimeout(() => {
               setIsProcessing(false);
            }, 5500);
         },
         onError: (error: AxiosError) => {
            console.error("Mutation error:", error.message);
            raiseRequestErrors();
            setIsProcessing(false);
         }
      }
   );

   const onClick = useCallback(() => {
      if (isProcessing) return;

      if (selectedItems.length === 0) {
         ToastService.warning("No items selected for refresh");
         return;
      }

      mutate(selectedItems);
   }, [selectedItems, mutate, isProcessing]);

   return (
      <StyledTooltip title="Refresh items from PIA">
         <span>
            <StyledToolbarButton
               ssrIcon={refreshIcon}
               text="Refresh"
               iconOnly={true}
               small={true}
               onClick={onClick}
               disabled={selectedItems.length === 0 || isProcessing}
            />
         </span>
      </StyledTooltip>
   );
};